<script setup lang="ts">
/**
 * Root Application Component -- Product Management Portal
 */
import { onMounted } from 'vue';
import { RouterView } from 'vue-router';
import { ConfirmDialog } from '@/shared/components';
import { AppToast, useIdleTimeout, useSessionSync } from '@tms-phoenix/ui-vue';
import { useUIStore, useAuthStore } from '@/stores';

const uiStore = useUIStore();
const authStore = useAuthStore();

useIdleTimeout(
  async () => {
    await authStore.logout();
    // logout() already calls router.push('/login') internally
  },
  { active: () => authStore.isAuthenticated },
);

// Sign this tab out when another tab ends or replaces the stored session (e.g.
// a different person signs in after this one was signed out), so a stale tab
// cannot keep acting as — or write back the tokens of — the previous user.
useSessionSync({
  storageKey: 'pinia-auth',
  getUserId: () => authStore.user?.id ?? null,
});

onMounted(() => {
  uiStore.applyTheme();
});
</script>

<template>
  <div
    id="app"
    class="h-screen overflow-hidden bg-app-gradient font-sans"
  >
    <!-- Router view with transitions -->
    <RouterView v-slot="{ Component, route }">
      <Transition
        :name="route.meta.transition as string || 'fade'"
        mode="out-in"
      >
        <component
          :is="Component"
          :key="route.path"
        />
      </Transition>
    </RouterView>

    <!-- Global Components -->
    <AppToast />
    <ConfirmDialog />
  </div>
</template>

<style>
/* Prevent page scroll */
html, body {
  overflow: hidden;
  height: 100vh;
  margin: 0;
  padding: 0;
}

/* App gradient background */
.bg-app-gradient {
  background: linear-gradient(to bottom right, var(--color-primary-100), var(--color-profile-50));
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide transition */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.2s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(-10px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(10px);
}
</style>
